import React from "react";
import { CollectionOrder, CollectionOrderItem } from "../../../api/collection";
import useCollection from "../hooks/useCollection";
import CollectionsListAvatar from "./CollectionsListAvatar";
import useDeviceType from "components/hooks/useDeviceType";
import DSBox from "../../system-design/DSBox";
import DSTypography from "../../system-design/DSTypography";
import DSChip from "../../system-design/DSChip";
import DSDialog from "../../system-design/DSDialog";
import DSDialogTitle from "../../system-design/DSDialogTitle";
import DSDialogContent from "../../system-design/DSDialogContent";
import DSDialogActions from "../../system-design/DSDialogActions";
import DSSecondaryButton from "../../system-design/DSSecondaryButton";
import {
  DSTable,
  DSTableHead,
  DSTableBody,
  DSTableRow,
  DSTableCell,
  DSTableContainer,
} from "../../system-design/Table";
import { sectionCardSx } from "../../system-design/sectionCardSx";

const sectionTitleSx = {
  fontSize: 12,
  fontFamily: "Gilroy-SemiBold",
  textTransform: "uppercase",
  letterSpacing: "0.12em",
  color: "surface.accentText",
};

const sectionTitleBarSx = {
  width: 28,
  height: 2,
  bgcolor: "primary.main",
  borderRadius: 2,
  mt: 0.75,
  mb: 1.5,
};

// Its own component because each row loads its collection by id (a hook can't
// be called inside `.map`). By id works for any collection, not only the
// approved ones the Collections list shows.
const CollectionOrderRow: React.FC<{
  item: CollectionOrderItem;
  emailSent: boolean;
}> = ({ item, emailSent }) => {
  const { data: collection, isLoading } = useCollection(item.collectionId);

  let name = "Collection not found";
  if (isLoading) name = "Loading…";
  else if (collection) name = collection.name;

  return (
    <DSTableRow>
      <DSTableCell sx={{ p: "5px" }}>
        <CollectionsListAvatar collectionCover={collection?.coverUrl} />
      </DSTableCell>
      <DSTableCell>{name}</DSTableCell>
      <DSTableCell>{item.collectionId}</DSTableCell>
      <DSTableCell>
        <code>{item.code}</code>
      </DSTableCell>
      <DSTableCell>
        {emailSent ? (
          <DSChip size="small" color="success" label="Sent" />
        ) : (
          <DSChip size="small" label="Not Sent" />
        )}
      </DSTableCell>
    </DSTableRow>
  );
};

interface CollectionOrderModalProps {
  show: boolean;
  order: CollectionOrder | null;
  onClose: () => void;
}

const CollectionOrderModal: React.FC<CollectionOrderModalProps> = ({
  show,
  order,
  onClose,
}) => {
  const { isSmallTablet } = useDeviceType();

  if (!order) return null;

  return (
    <DSDialog
      open={show}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isSmallTablet}
    >
      <DSDialogTitle
        variant="h5"
        sx={{ borderBottom: "1px solid", borderBottomColor: "divider" }}
      >
        Order Details - {order.orderName}
      </DSDialogTitle>
      <DSDialogContent>
        <DSBox sx={{ mb: 3 }}>
          <DSTypography variant="h6" sx={sectionTitleSx}>
            Order Information
          </DSTypography>
          <DSBox sx={sectionTitleBarSx} />
          <DSTableContainer sx={sectionCardSx}>
            <DSTable size="small">
              <DSTableBody>
                <DSTableRow>
                  <DSTableCell>
                    <DSTypography variant="body2" fontWeight={700}>
                      Order Name:
                    </DSTypography>
                  </DSTableCell>
                  <DSTableCell>{order.orderName}</DSTableCell>
                </DSTableRow>
                <DSTableRow>
                  <DSTableCell>
                    <DSTypography variant="body2" fontWeight={700}>
                      Status:
                    </DSTypography>
                  </DSTableCell>
                  <DSTableCell>
                    <DSChip
                      size="small"
                      color={
                        order.status === "completed" ? "success" : "warning"
                      }
                      label={order.status}
                    />
                  </DSTableCell>
                </DSTableRow>
                <DSTableRow>
                  <DSTableCell>
                    <DSTypography variant="body2" fontWeight={700}>
                      Order Date:
                    </DSTypography>
                  </DSTableCell>
                  <DSTableCell>
                    {new Date(order.orderCreationDate).toLocaleString()}
                  </DSTableCell>
                </DSTableRow>
                <DSTableRow>
                  <DSTableCell>
                    <DSTypography variant="body2" fontWeight={700}>
                      Customer Email:
                    </DSTypography>
                  </DSTableCell>
                  <DSTableCell>{order.customerEmail}</DSTableCell>
                </DSTableRow>
                <DSTableRow>
                  <DSTableCell>
                    <DSTypography variant="body2" fontWeight={700}>
                      Customer Name:
                    </DSTypography>
                  </DSTableCell>
                  <DSTableCell>{order.customerName}</DSTableCell>
                </DSTableRow>
              </DSTableBody>
            </DSTable>
          </DSTableContainer>
        </DSBox>

        <DSBox>
          <DSTypography variant="h6" sx={sectionTitleSx}>
            Collections ({order.collections.length})
          </DSTypography>
          <DSBox sx={sectionTitleBarSx} />
          <DSTableContainer sx={sectionCardSx}>
            <DSTable size="small">
              <DSTableHead>
                <DSTableRow>
                  <DSTableCell>Cover</DSTableCell>
                  <DSTableCell>Collection Name</DSTableCell>
                  <DSTableCell>Collection ID</DSTableCell>
                  <DSTableCell>Code</DSTableCell>
                  <DSTableCell>Email Sent</DSTableCell>
                </DSTableRow>
              </DSTableHead>
              <DSTableBody>
                {order.collections.map((collection, index) => (
                  <CollectionOrderRow
                    key={index}
                    item={collection}
                    emailSent={!!order.collectionEmailSent}
                  />
                ))}
              </DSTableBody>
            </DSTable>
          </DSTableContainer>
        </DSBox>
      </DSDialogContent>
      <DSDialogActions>
        <DSSecondaryButton onClick={onClose}>Close</DSSecondaryButton>
      </DSDialogActions>
    </DSDialog>
  );
};

export default CollectionOrderModal;
